import React, { useState, useEffect } from 'react';
import { Trophy, Medal, Loader2, Clock, History, X, Info } from 'lucide-react';
import {
  PRIZES,
  LEGACY_PRIZES,
  REWARD_SCALING,
  PRIZE_DISTRIBUTION,
  useLeaderboard,
  fetchPreviousLeaderboard,
  PreviousLeaderboardResponse
} from '../services/leaderboardService';

// Countdown Timer Component
interface CountdownTimerProps {
  endDate: Date;
}

interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

// Explanation Modal Component
interface ExplanationModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// Previous Leaderboard Modal Component
interface PreviousLeaderboardModalProps {
  isOpen: boolean;
  onClose: () => void;
  data: PreviousLeaderboardResponse | null;
  isLoading: boolean;
  error: string | null;
}

const ExplanationModal: React.FC<ExplanationModalProps> = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn">
      <div className="bg-gradient-to-b from-zinc-800/90 to-zinc-900/90 rounded-xl border border-[#D7FF00]/20 max-w-3xl w-full max-h-[90vh] overflow-y-auto animate-scaleIn">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-2xl font-bold flex items-center gap-2">
              <Info className="text-[#D7FF00]" />
              Leaderboard Explanation
            </h3>
            <button onClick={onClose} className="text-gray-400 hover:text-white p-1 rounded-full hover:bg-white/10 transition-colors">
              <X size={20} />
            </button>
          </div>

          <div className="space-y-6 text-gray-200">
            <p>
              If you sign up using code "VICO" you'll get exclusive access to my monthly leaderboards, where the top 10 can win up to $4,750 every single month!
            </p>

            <p>
              It's a dynamic leaderboard, meaning the total wager volume from you + other affiliates determines how big the prize pool gets.
            </p>

            <div className="mt-4">
              <h4 className="text-lg font-semibold mb-3 text-[#D7FF00]">Reward Scaling System:</h4>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-zinc-700/50 text-left">
                      <th className="p-3 text-sm font-semibold">Wagered</th>
                      <th className="p-3 text-sm font-semibold">Reward</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-700/30">
                    {REWARD_SCALING.map((tier, index) => (
                      <tr key={index} className="hover:bg-white/5 transition-colors">
                        <td className="p-3 text-sm">${tier.wagered.toLocaleString()}.00</td>
                        <td className="p-3 text-sm text-[#D7FF00]">${tier.reward.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const PreviousLeaderboardModal: React.FC<PreviousLeaderboardModalProps> = ({ isOpen, onClose, data, isLoading, error }) => {
  if (!isOpen) return null;

  const formatWager = (wager: number) => {
    return wager.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn">
      <div className="bg-gradient-to-b from-zinc-800/90 to-zinc-900/90 rounded-xl border border-[#D7FF00]/20 max-w-3xl w-full max-h-[90vh] overflow-y-auto animate-scaleIn">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-2xl font-bold flex items-center gap-2">
              <History className="text-[#D7FF00]" />
              Previous Winners
            </h3>
            <button onClick={onClose} className="text-gray-400 hover:text-white p-1 rounded-full hover:bg-white/10 transition-colors">
              <X size={20} />
            </button>
          </div>

          {isLoading && (
            <div className="flex justify-center items-center py-12">
              <Loader2 className="animate-spin h-8 w-8 text-[#D7FF00]" />
            </div>
          )}

          {error && (
            <div className="text-center py-8">
              <p className="text-red-400">{error}</p>
            </div>
          )}

          {!isLoading && !error && data && (
            <>
              <div className="mb-6">
                <p className="text-gray-300">
                  Period: <span className="text-[#D7FF00]">{new Date(data.period.start).toLocaleDateString()}</span> to <span className="text-[#D7FF00]">{new Date(data.period.end).toLocaleDateString()}</span>
                </p>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-zinc-700/50 text-left">
                      <th className="p-3 text-sm md:text-base">Rank</th>
                      <th className="p-3 text-sm md:text-base">Player</th>
                      <th className="p-3 text-sm md:text-base text-right">Wager</th>
                      <th className="p-3 text-sm md:text-base text-right">Prize</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-700/30">
                    {data.data.map((player) => (
                      <tr key={player.uid} className="hover:bg-white/5 transition-colors duration-200">
                        <td className="p-3 text-sm md:text-base font-medium">
                          <span className="flex items-center gap-2">
                            #{player.rank}
                            {player.rank === 1 && <Trophy size={16} className="text-[#D7FF00]" />}
                            {player.rank === 2 && <Medal size={16} className="text-[#C0C0C0]" />}
                            {player.rank === 3 && <Medal size={16} className="text-[#CD7F32]" />}
                          </span>
                        </td>
                        <td className="p-3 text-sm md:text-base font-medium">
                          {player.name}
                        </td>
                        <td className="p-3 text-sm md:text-base text-right">
                          ${formatWager(player.wager)}
                        </td>
                        <td className="p-3 text-sm md:text-base text-right font-bold text-[#D7FF00]">
                          ${player.prize}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const CountdownTimer: React.FC<CountdownTimerProps> = ({ endDate }) => {
  const [timeLeft, setTimeLeft] = useState<TimeLeft>({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  useEffect(() => {
    // Calculate time left function
    const calculateTimeLeft = () => {
      const difference = endDate.getTime() - new Date().getTime();

      if (difference <= 0) {
        return { days: 0, hours: 0, minutes: 0, seconds: 0 };
      }

      return {
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((difference / 1000 / 60) % 60),
        seconds: Math.floor((difference / 1000) % 60)
      };
    };

    // Set initial time
    setTimeLeft(calculateTimeLeft());

    // Update every second
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    // Clean up interval
    return () => clearInterval(timer);
  }, [endDate]);

  return (
    <div>
      {/* Mobile view */}
      <div className="md:hidden">
        <div className="grid grid-cols-2 gap-2">
          <div className="bg-[#D7FF00]/5 border border-[#D7FF00]/20 rounded-lg p-2 flex items-center justify-between">
            <span className="text-xs text-gray-400">Days</span>
            <span className="text-lg font-bold">{String(timeLeft.days).padStart(2, '0')}</span>
          </div>
          <div className="bg-[#D7FF00]/5 border border-[#D7FF00]/20 rounded-lg p-2 flex items-center justify-between">
            <span className="text-xs text-gray-400">Hours</span>
            <span className="text-lg font-bold">{String(timeLeft.hours).padStart(2, '0')}</span>
          </div>
          <div className="bg-[#D7FF00]/5 border border-[#D7FF00]/20 rounded-lg p-2 flex items-center justify-between">
            <span className="text-xs text-gray-400">Minutes</span>
            <span className="text-lg font-bold">{String(timeLeft.minutes).padStart(2, '0')}</span>
          </div>
          <div className="bg-[#D7FF00]/5 border border-[#D7FF00]/20 rounded-lg p-2 flex items-center justify-between">
            <span className="text-xs text-gray-400">Seconds</span>
            <span className="text-lg font-bold">{String(timeLeft.seconds).padStart(2, '0')}</span>
          </div>
        </div>
      </div>

      {/* Desktop view */}
      <div className="hidden md:flex items-center justify-center gap-4">
        <Clock className="text-[#D7FF00] mr-2" />
        <div className="bg-[#D7FF00]/5 border border-[#D7FF00]/20 rounded-lg p-3 flex flex-col items-center w-20">
          <span className="text-2xl font-bold">{String(timeLeft.days).padStart(2, '0')}</span>
          <span className="text-xs text-gray-400 mt-1">Days</span>
        </div>
        <span className="text-xl">:</span>
        <div className="bg-[#D7FF00]/5 border border-[#D7FF00]/20 rounded-lg p-3 flex flex-col items-center w-20">
          <span className="text-2xl font-bold">{String(timeLeft.hours).padStart(2, '0')}</span>
          <span className="text-xs text-gray-400 mt-1">Hours</span>
        </div>
        <span className="text-xl">:</span>
        <div className="bg-[#D7FF00]/5 border border-[#D7FF00]/20 rounded-lg p-3 flex flex-col items-center w-20">
          <span className="text-2xl font-bold">{String(timeLeft.minutes).padStart(2, '0')}</span>
          <span className="text-xs text-gray-400 mt-1">Minutes</span>
        </div>
        <span className="text-xl">:</span>
        <div className="bg-[#D7FF00]/5 border border-[#D7FF00]/20 rounded-lg p-3 flex flex-col items-center w-20">
          <span className="text-2xl font-bold">{String(timeLeft.seconds).padStart(2, '0')}</span>
          <span className="text-xs text-gray-400 mt-1">Seconds</span>
        </div>
      </div>
    </div>
  );
};

const Leaderboard = () => {
  // State for modals
  const [showPreviousModal, setShowPreviousModal] = useState(false);
  const [showExplanationModal, setShowExplanationModal] = useState(false);
  const [previousLeaderboard, setPreviousLeaderboard] = useState<PreviousLeaderboardResponse | null>(null);
  const [previousLeaderboardLoading, setPreviousLeaderboardLoading] = useState(false);
  const [previousLeaderboardError, setPreviousLeaderboardError] = useState<string | null>(null);

  // Use the leaderboard hook to fetch the data
  const {
    leaderboardData,
    error,
    loading,
    lastUpdated,
    cycleStartDate,
    cycleEndDate,
    totalPrizePool,
    totalWagered,
    prizes
  } = useLeaderboard();

  // Pre-fetch the previous leaderboard data when component mounts
  useEffect(() => {
    fetchPreviousLeaderboard()
      .then(data => {
        setPreviousLeaderboard(data);
      })
      .catch(err => {
        console.error('Error pre-fetching previous leaderboard:', err);
        setPreviousLeaderboardError('Failed to load previous winners');
      });
  }, []);

  // Format the cycle period dates
  const formattedPeriod = {
    start: cycleStartDate?.toLocaleDateString(),
    end: cycleEndDate?.toLocaleDateString()
  };

  // Get top 3 players and the rest of the list
  const topThree = leaderboardData.slice(0, 3);
  const restOfList = leaderboardData.slice(3, 10);

  // Display a loader while data is being fetched
  if (loading) {
    return (
      <div id="leaderboard-section" className="py-16 px-4 flex justify-center items-center min-h-[500px]">
        <div className="text-center">
          <Loader2 className="animate-spin h-12 w-12 mb-4 mx-auto text-[#D7FF00]" />
          <p className="text-lg text-gray-400">Loading Goated Leaderboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div id="leaderboard-section" className="py-16 px-4 scroll-mt-20">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <div className="flex flex-col md:flex-row justify-between items-center mb-6">
            <div className="flex items-center gap-2">
              <h2 className="text-3xl md:text-4xl font-bold mb-4 md:mb-0 bg-gradient-to-r from-[#D7FF00] to-white bg-clip-text text-transparent">
                Goated Leaderboard
              </h2>
              <button
                onClick={() => setShowExplanationModal(true)}
                className="inline-flex items-center justify-center h-8 w-8 rounded-full bg-[#D7FF00]/20 hover:bg-[#D7FF00]/30 border border-[#D7FF00]/30 transition-colors"
                title="Learn how the dynamic prize pool works"
              >
                <Info size={16} className="text-[#D7FF00]" />
              </button>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowExplanationModal(true)}
                className="bg-gradient-to-r from-[#D7FF00]/20 to-[#D7FF00]/10 hover:from-[#D7FF00]/30 hover:to-[#D7FF00]/20 text-white py-2 px-4 rounded-md transition duration-300 flex items-center gap-2 border border-[#D7FF00]/30"
              >
                <Info size={16} />
                Info / Rules
              </button>
            </div>
          </div>

          <div className="mb-6">
            <p className="text-gray-400 mb-2">
              Period: {formattedPeriod.start} - {formattedPeriod.end}
            </p>
            {lastUpdated && (
              <p className="text-gray-500 text-sm">
                Last updated: {lastUpdated.toLocaleString()}
              </p>
            )}
          </div>

          <div className="mb-4">
            <p className="text-gray-400">
              Top players competing for ${totalPrizePool.toFixed(2)} in prizes
            </p>
            <p className="text-gray-500 text-sm mt-1">
              Total wagered: ${totalWagered.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
            {error && (
              <p className="text-yellow-500 mt-2 text-sm">
                {error}
              </p>
            )}
          </div>
        </div>

        {/* Top 3 Players - Podium Style */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
          {/* 2nd Place */}
          <div className="md:mt-16">
            <div className="relative bg-gradient-to-b from-white/10 to-white/5 p-6 rounded-2xl border border-[#C0C0C0]/30 backdrop-blur-sm transform hover:scale-105 transition-all duration-300 group">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <div className="relative">
                  <div className="absolute inset-0 rounded-full blur-md bg-[#C0C0C0]/50"></div>
                  <Medal className="w-8 h-8 text-[#C0C0C0] relative z-10" />
                </div>
              </div>
              <div className="text-center mt-4">
                <span className="text-[#C0C0C0] text-xl font-bold">#2</span>
                <h3 className="text-xl font-bold mb-2 group-hover:text-[#C0C0C0] transition-colors">
                  {topThree[1]?.username || 'N/A'}
                </h3>
                <p className="text-gray-400 mb-2">
                  ${topThree[1]?.wagered.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}
                </p>
                <p className="text-[#D7FF00] font-bold">
                  Prize: ${prizes[2] ? prizes[2].toFixed(2) : "0.00"}
                </p>
              </div>
            </div>
          </div>

          {/* 1st Place */}
          <div>
            <div className="relative bg-gradient-to-b from-[#D7FF00]/10 to-transparent p-8 rounded-2xl border border-[#D7FF00]/30 backdrop-blur-sm transform hover:scale-105 transition-all duration-300 group">
              <div className="absolute -top-6 left-1/2 transform -translate-x-1/2">
                <div className="relative">
                  <div className="absolute inset-0 rounded-full blur-lg bg-[#D7FF00]/50"></div>
                  <Trophy className="w-12 h-12 text-[#D7FF00] relative z-10" />
                </div>
              </div>
              <div className="text-center mt-6">
                <span className="text-[#D7FF00] text-2xl font-bold">#1</span>
                <h3 className="text-2xl font-bold mb-2 group-hover:text-[#D7FF00] transition-colors">
                  {topThree[0]?.username || 'N/A'}
                </h3>
                <p className="text-gray-400 mb-2">
                  ${topThree[0]?.wagered.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}
                </p>
                <p className="text-[#D7FF00] font-bold text-lg">
                  Prize: ${prizes[1] ? prizes[1].toFixed(2) : "0.00"}
                </p>
              </div>
            </div>
          </div>

          {/* 3rd Place */}
          <div className="md:mt-24">
            <div className="relative bg-gradient-to-b from-white/10 to-white/5 p-6 rounded-2xl border border-[#CD7F32]/30 backdrop-blur-sm transform hover:scale-105 transition-all duration-300 group">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <div className="relative">
                  <div className="absolute inset-0 rounded-full blur-md bg-[#CD7F32]/50"></div>
                  <Medal className="w-8 h-8 text-[#CD7F32] relative z-10" />
                </div>
              </div>
              <div className="text-center mt-4">
                <span className="text-[#CD7F32] text-xl font-bold">#3</span>
                <h3 className="text-xl font-bold mb-2 group-hover:text-[#CD7F32] transition-colors">
                  {topThree[2]?.username || 'N/A'}
                </h3>
                <p className="text-gray-400 mb-2">
                  ${topThree[2]?.wagered.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}
                </p>
                <p className="text-[#D7FF00] font-bold">
                  Prize: ${prizes[3] ? prizes[3].toFixed(2) : "0.00"}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Countdown Timer */}
        <div className="bg-gradient-to-r from-[#D7FF00]/10 to-white/5 p-4 sm:p-6 rounded-xl border border-[#D7FF00]/20 mb-8 text-center">
          <h3 className="text-lg sm:text-xl font-semibold mb-2">Leaderboard Cycle Ends In</h3>
          <CountdownTimer endDate={cycleEndDate} />
          <p className="text-gray-400 mt-3 text-xs sm:text-sm">
            New competition cycle starts immediately after the current one ends
          </p>
        </div>

        {/* Leaderboard Rules */}
        <div className="bg-gradient-to-r from-white/10 to-[#D7FF00]/5 p-4 sm:p-6 rounded-xl border border-white/20 mb-8">
          <h3 className="text-lg sm:text-xl font-semibold mb-4 text-center">Leaderboard Rules</h3>
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="min-w-5 pt-1">
                <div className="h-5 w-5 rounded-full bg-[#D7FF00]/20 flex items-center justify-center">
                  <span className="text-xs text-[#D7FF00] font-bold">1</span>
                </div>
              </div>
              <p className="text-sm sm:text-base">
                Participants must wager a minimum of $25,000 to qualify for prizes.
              </p>
            </div>
            <div className="flex items-start gap-3">
              <div className="min-w-5 pt-1">
                <div className="h-5 w-5 rounded-full bg-[#D7FF00]/20 flex items-center justify-center">
                  <span className="text-xs text-[#D7FF00] font-bold">2</span>
                </div>
              </div>
              <div className="text-sm sm:text-base">
                <p>We are fair to our users, please do not abuse the leaderboard. Attempts to abuse the leaderboard will be voided, and banned.</p>
                <p className="text-[#D7FF00]/80 font-medium mt-1">Please play responsibly and fairly.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Rest of the Leaderboard */}
        <div className="bg-gradient-to-b from-white/10 to-transparent rounded-2xl border border-white/10 overflow-hidden backdrop-blur-sm">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-white/5">
                  <th className="px-6 py-4 text-left text-sm font-semibold">Rank</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Player</th>
                  <th className="px-6 py-4 text-right text-sm font-semibold">Wagered</th>
                  <th className="px-6 py-4 text-right text-sm font-semibold">Prize</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {restOfList.map((player) => (
                  <tr
                    key={player.rank}
                    className="hover:bg-white/5 transition-colors duration-200"
                  >
                    <td className="px-6 py-4 text-sm">
                      <span className="flex items-center gap-2">
                        #{player.rank}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm font-medium">{player.username}</td>
                    <td className="px-6 py-4 text-sm text-right">
                      ${player.wagered.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td className="px-6 py-4 text-sm text-right">
                      <span className="text-[#D7FF00] font-bold">${prizes[player.rank] ? prizes[player.rank].toFixed(2) : "0.00"}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Previous Leaderboard Modal */}
      <PreviousLeaderboardModal
        isOpen={showPreviousModal}
        onClose={() => setShowPreviousModal(false)}
        data={previousLeaderboard}
        isLoading={previousLeaderboardLoading}
        error={previousLeaderboardError}
      />

      {/* Explanation Modal */}
      <ExplanationModal
        isOpen={showExplanationModal}
        onClose={() => setShowExplanationModal(false)}
      />
    </div>
  );
};

export default Leaderboard;